/**
 * Small primitives the editor's panes share: buttons with a visible focus
 * ring and pressed state at 44×44 or larger, status as icon + word, the 1s
 * change highlight, and a modal sheet.
 */
import React, {useEffect, useRef, useState} from 'react';
import {
  AccessibilityInfo,
  Animated,
  Pressable,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {F, T, S} from '../courseTheme';
import {E} from './editorTheme';
import type {StatusView} from './editorLogic';

export function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    let alive = true;
    // Guard the result, not just the method: the RN jest mock returns undefined.
    Promise.resolve(AccessibilityInfo.isReduceMotionEnabled?.())
      // Only a real change sets state: the usual answer is "no", and setting
      // the same value after mount is a needless re-render.
      .then(v => {
        if (alive && v) setReduced(true);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return reduced;
}

// ---- keyboard focus only (the :focus-visible rule) -------------------------
// A ring after a mouse click is noise; after a Tab it is the only way to see
// where you are. The last input decides. Without a DOM (jest, native) every
// focus shows its ring.
let keyboardModality = true;
let listening = false;
function listenForModality() {
  if (listening) return;
  const doc = (globalThis as any).document;
  if (!doc?.addEventListener) return;
  listening = true;
  doc.addEventListener(
    'pointerdown',
    () => {
      keyboardModality = false;
    },
    true,
  );
  doc.addEventListener(
    'mousedown',
    () => {
      keyboardModality = false;
    },
    true,
  );
  doc.addEventListener(
    'keydown',
    (e: any) => {
      if (!e.metaKey && !e.ctrlKey && !e.altKey) keyboardModality = true;
    },
    true,
  );
}
/** Whether a focus that just happened should show its ring. */
export function focusVisible(): boolean {
  listenForModality();
  return keyboardModality;
}

/** A focus ring for keyboard users; RN's style types do not know outline. */
export const ring = (on: boolean, inset = false) =>
  ({
    outlineWidth: on ? 2 : 0,
    outlineColor: E.accent,
    outlineStyle: 'solid',
    outlineOffset: inset ? -2 : 2,
  } as any);

/** Web transition for state changes, dropped under reduced motion. */
export const motion = (reduced: boolean) =>
  reduced
    ? null
    : ({
        transitionDuration: '150ms',
        transitionProperty: 'background-color, border-color, opacity',
      } as any);

type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'accent';

const BUTTON: Record<
  ButtonVariant,
  {bg: string; pressed: string; fg: string; border: string}
> = {
  // The one loud thing on screen: Publish, and only Publish.
  primary: {
    bg: F.yellow,
    pressed: '#EDC23A',
    fg: E.ink,
    border: F.yellowBorder,
  },
  secondary: {
    bg: E.surface,
    pressed: '#EEF1F0',
    fg: E.ink,
    border: 'rgba(38,35,34,0.22)',
  },
  quiet: {
    bg: 'transparent',
    pressed: E.pressed,
    fg: E.ink,
    border: 'transparent',
  },
  accent: {bg: E.accent, pressed: '#225F58', fg: F.white, border: E.accent},
};

export function Button({
  label,
  onPress,
  icon,
  trailingIcon,
  variant = 'secondary',
  disabled = false,
  accessibilityLabel,
  accessibilityState,
  accessibilityHint,
  testID,
  style,
}: {
  label: string;
  onPress?: () => void;
  icon?: string;
  trailingIcon?: string;
  variant?: ButtonVariant;
  disabled?: boolean;
  accessibilityLabel?: string;
  accessibilityState?: Record<string, unknown>;
  accessibilityHint?: string;
  testID?: string;
  style?: StyleProp<ViewStyle>;
}) {
  const [focused, setFocused] = useState(false);
  const t = BUTTON[variant];
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityHint={accessibilityHint}
      accessibilityState={{disabled, ...(accessibilityState ?? {})}}
      disabled={disabled}
      onPress={() => {
        if (!disabled) onPress?.();
      }}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={({pressed, hovered}: any) => [
        {
          minHeight: 44,
          minWidth: 44,
          paddingHorizontal: S[4],
          borderRadius: 10,
          borderWidth: 1,
          borderColor: t.border,
          backgroundColor: pressed
            ? t.pressed
            : hovered && variant !== 'accent' && variant !== 'primary'
            ? E.hover
            : t.bg,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: S[2],
          opacity: disabled ? 0.45 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        } as any,
        ring(focused),
        style,
      ]}>
      {icon ? <Ionicons name={icon} size={18} color={t.fg} /> : null}
      <CCText style={{fontSize: T.sm + 1, fontWeight: '600', color: t.fg}}>
        {label}
      </CCText>
      {trailingIcon ? (
        <Ionicons name={trailingIcon} size={16} color={t.fg} />
      ) : null}
    </Pressable>
  );
}

export function IconButton({
  icon,
  label,
  onPress,
  disabled = false,
  color = E.ink,
  size = 44,
  accessibilityState,
}: {
  icon: string;
  label: string;
  onPress?: () => void;
  disabled?: boolean;
  color?: string;
  size?: number;
  accessibilityState?: Record<string, unknown>;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{disabled, ...(accessibilityState ?? {})}}
      disabled={disabled}
      onPress={() => {
        if (!disabled) onPress?.();
      }}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={({pressed, hovered}: any) => [
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: pressed
            ? E.pressed
            : hovered
            ? E.hover
            : 'transparent',
          opacity: disabled ? 0.45 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        } as any,
        ring(focused),
      ]}>
      <Ionicons name={icon} size={20} color={color} />
    </Pressable>
  );
}

/** Status as an icon, a colour and a word — never colour alone. */
export function StatusMark({
  status,
  size = 14,
}: {
  status: Pick<StatusView, 'icon' | 'fg' | 'word'>;
  size?: number;
}) {
  return (
    <View style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
      <Ionicons name={status.icon} size={size} color={status.fg} />
      <CCText style={{fontSize: T.xs, fontWeight: '600', color: status.fg}}>
        {status.word}
      </CCText>
    </View>
  );
}

/**
 * The 1s highlight on text an edit just changed. Under reduced motion the
 * highlight still shows for its second — it is information, not decoration —
 * it just does not fade.
 */
export function Flash({
  active,
  field,
  reduced,
  children,
  style,
}: {
  active: boolean;
  field: string;
  reduced: boolean;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  const fade = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!active) {
      fade.setValue(0);
      return;
    }
    fade.setValue(1);
    if (reduced) return;
    const anim = Animated.timing(fade, {
      toValue: 0,
      duration: 1000,
      delay: 200,
      useNativeDriver: false,
    });
    anim.start();
    return () => anim.stop();
  }, [active, reduced, fade]);
  return (
    <View style={[{position: 'relative', borderRadius: 6}, style]}>
      {active ? (
        <Animated.View
          testID={`changed-${field}`}
          pointerEvents="none"
          style={{
            position: 'absolute',
            left: -6,
            right: -6,
            top: -2,
            bottom: -2,
            borderRadius: 6,
            backgroundColor: E.flash,
            opacity: fade,
          }}
        />
      ) : null}
      {children}
    </View>
  );
}

/**
 * A modal sheet over the whole editor. Escape and the close button dismiss it;
 * the scrim is 50% black so the sheet reads on its own.
 */
export function Sheet({
  title,
  onClose,
  children,
  width = 560,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
}) {
  useEscape(onClose);
  return (
    <View
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: 50,
        alignItems: 'center',
        justifyContent: 'center',
        padding: S[4],
      }}>
      {/* The scrim closes the sheet on a click; keyboard and screen-reader
          users have the labelled close button and Escape instead. */}
      <Pressable
        accessible={false}
        focusable={false}
        onPress={onClose}
        style={
          {
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            backgroundColor: E.scrim,
            cursor: 'default',
          } as any
        }
      />
      <View
        accessibilityViewIsModal
        accessibilityLabel={title}
        style={{
          width: '100%',
          maxWidth: width,
          maxHeight: '92%',
          backgroundColor: E.surface,
          borderRadius: 16,
          overflow: 'hidden',
          shadowColor: '#000',
          shadowOffset: {width: 0, height: 12},
          shadowOpacity: 0.18,
          shadowRadius: 32,
          elevation: 12,
        }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingLeft: S[5],
            paddingRight: S[2],
            paddingVertical: S[2],
            borderBottomWidth: 1,
            borderBottomColor: E.line,
          }}>
          <CCText
            accessibilityRole="header"
            style={{flex: 1, fontSize: T.lg, fontWeight: '700', color: E.ink}}>
            {title}
          </CCText>
          <IconButton
            icon="close"
            label={`Close ${title.toLowerCase()}`}
            onPress={onClose}
          />
        </View>
        {children}
      </View>
    </View>
  );
}

/** Calls `onEscape` on the Escape key (web only; a no-op without a document). */
export function useEscape(onEscape: () => void) {
  const ref = useRef(onEscape);
  ref.current = onEscape;
  useEffect(() => {
    const doc = (globalThis as any).document;
    if (!doc?.addEventListener) return undefined;
    const handler = (e: any) => {
      if (e?.key === 'Escape') ref.current();
    };
    doc.addEventListener('keyup', handler);
    return () => doc.removeEventListener('keyup', handler);
  }, []);
}

/** Text only screen readers see, for live announcements. */
export const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
} as any;

/** A calm inline notice — an error or a result — announced politely. */
export function Notice({
  tone = 'warn',
  text,
}: {
  tone?: 'warn' | 'bad' | 'info';
  text: string;
}) {
  const fg = tone === 'bad' ? '#C0392B' : tone === 'warn' ? '#B45309' : E.ink;
  const icon =
    tone === 'info' ? 'information-circle-outline' : 'alert-circle-outline';
  return (
    <View
      accessibilityLiveRegion="polite"
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: S[2],
        paddingVertical: S[2],
        paddingHorizontal: S[3],
        borderRadius: 10,
        backgroundColor: E.surface,
        borderWidth: 1,
        borderColor: E.line,
      }}>
      <Ionicons name={icon} size={18} color={fg} />
      <CCText style={{flex: 1, fontSize: T.sm, lineHeight: 19, color: E.ink}}>
        {text}
      </CCText>
    </View>
  );
}
